import { useCallback } from 'react';

// The hostname-to-site-key mapping is single-sourced in public/index.html,
// which resolves the key for the current hostname (localhost -> Google's
// public test key, staging -> placeholder, everything else -> production),
// exposes it as window.__RECAPTCHA_SITE_KEY__, and loads the reCAPTCHA
// script with it. This hook only reads that resolved key - do not add a
// second copy of the mapping here.
const getSiteKeyFromPage = () => {
    if (typeof window === 'undefined') return undefined;
    return window.__RECAPTCHA_SITE_KEY__;
};

const useRecaptcha = () => {
    const getSiteKey = useCallback(() => {
        return getSiteKeyFromPage();
    }, []);

    const executeRecaptcha = useCallback(async (action = 'submit') => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha) {
                reject(new Error('reCAPTCHA not loaded'));
                return;
            }

            const siteKey = getSiteKey();
            if (!siteKey) {
                reject(new Error('reCAPTCHA site key not available'));
                return;
            }

            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(siteKey, { action })
                    .then((token) => {
                        resolve(token);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        });
    }, [getSiteKey]);

    const isRecaptchaLoaded = useCallback(() => {
        return typeof window !== 'undefined' && window.grecaptcha;
    }, []);

    return {
        executeRecaptcha,
        isRecaptchaLoaded,
        getSiteKey
    };
};

export default useRecaptcha;
